import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Grid, Image } from "semantic-ui-react";
import { loginApi } from "../../api/login";
import { checkoutApi } from "../../api/checkout";
import "./style.scss";

import { loginSchema } from "../../utils/validator";
import * as yup from "yup";
import { toast } from "../../libs/toast";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import {
  fetchUserInfo,
  makeLogin,
  forgotPassword,
  loginWithOtpCode,
  logout,
} from "../../reducer/user";
import {
  createAccount,
  verifyPhoneNumber,
  createPassword,
  setChannelName,
} from "./actions";
import { resetBooking, setBooking } from "../../reducer/booking";
import { isPhoneNumber, isEmpty } from "../../utils/common";
import {
  RegisterKeyType,
  RegisterDataType,
  LoginActions,
  OfferItemType,
  StorageKEY,
} from "../../models";

import CheckOut from "./components/CheckOut";
import LoginForm from "./components/LoginForm";
import Modal from "../../components/Modal";
import ModalContent from "./components/ModalContent";
import { PAYMENT_CHANELS, PageName } from "../../constants";
import SliderSection from "./components/SliderSection";
import PaymentLoading from "./components/PaymentLoading";
import { isBooking } from "../../constants";
import { tracker } from "../../api/trackingService";
import { authToken } from "../../utils/storageVariables";
interface PropsType {
  children?: JSX.Element;
}

const ChanelPage: React.FC<PropsType> = (props) => {
  const { channelType } = useParams<{ channelType: PageName }>() || "";

  const dispatch = useAppDispatch();
  const isLogedin = useAppSelector((state) => state.userInfo.isLogedin);
  const userData = useAppSelector((state) => state.userInfo.profile);
  const ipAddress = useAppSelector((state) => state.chanel.ipAddress);
  const sessionId = useAppSelector((state) => state.chanel.sessionId);
  // const isBooking = useAppSelector((state) => state.booking.isBooking);
  const inputRef = useRef<HTMLInputElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);
  const timeOutRef = useRef<ReturnType<typeof setTimeout>>();
  const [searchParams] = useSearchParams();
  const [loginData, setLoginData] = useState<RegisterDataType>({
    phoneNumber: "",
    otpCode: "",
    password: "",
    token: "",
    rfToken: "",
    fromAction: LoginActions.CheckAccount,
    nextAction: LoginActions.CheckAccount,
  });
  const [formState, setFormState] = useState<{
    isShowPassword: boolean;
    isShowOTP: boolean;
    isShowModal: boolean;
    canResendOTP: boolean;
  }>({
    isShowPassword: false,
    isShowOTP: false,
    isShowModal: false,
    canResendOTP: true,
  });
  const [counter, setCounter] = useState<number>(0);
  const navigate = useNavigate();
  const [isPaymentFail, setPaymentFail] = useState(false);
  const [errors, setErrors] = useState<{
    [RegisterKeyType.phoneNumber]?: string;
    [RegisterKeyType.password]?: string;
    [RegisterKeyType.otpCode]?: string;
    responses?: string;
  }>({});

  const [isPaymentLoading, setPaymentLoading] = useState(false);
  const trackerParams = useMemo(() => {
    let mktCampaign = "";
    let eventLabel = "";

    switch (channelType) {
      case PageName.VNPAY: {
        eventLabel = "PNS-TVOD-GPVNP50k";
        mktCampaign = "pns_landing_vnp_nbn";
        break;
      }
      case PageName.SHOPEE: {
        eventLabel = "PNS-TVOD-GPSPP50k";
        mktCampaign = "pns_landing_spp_nbn";
        break;
      }
      case PageName.ZALO: {
        eventLabel = "PNS-TVOD-GPZLP50k";
        mktCampaign = "pns_landing_zlp_nbn";
        break;
      }
    }

    return {
      client_ip: ipAddress,
      session_id: sessionId,
      mkt_campaign: mktCampaign,
      event_label: eventLabel,
      direct_object_type: "screen",
      on_screen: "home",
      direct_object_property: "",
    };
  }, [ipAddress, sessionId, channelType]);
  const onResetAll = () => {
    setLoginData((prevState) => ({
      ...prevState,
      phoneNumber: "",
      password: "",
      otpCode: "",
      token: "",
      rfToken: "",
      fromAction: LoginActions.CheckAccount,
      nextAction: LoginActions.CheckAccount,
    }));
    setFormState({
      isShowPassword: false,
      isShowOTP: false,
      isShowModal: false,
      canResendOTP: true,
    });
    setErrors({});
  };
  const onChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    { key, value }: { key: string; value: string }
  ) => {
    if (
      key === RegisterKeyType.phoneNumber &&
      loginData.nextAction !== LoginActions.CheckAccount
    ) {
      onResetAll();
    }
    if (key === RegisterKeyType.phoneNumber) {
      if ((!isPhoneNumber(value) && value.length !== 0) || value.length > 10) {
        return;
      }
    }
    setLoginData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };
  const onForgotPassword = async () => {
    const response = await dispatch(
      forgotPassword(loginData.phoneNumber)
    ).unwrap();

    if (response.data.statusCode === 400) {
      setErrors(() => ({
        responses: response.data.message,
      }));
    } else {
      setLoginData((formData) => ({
        ...formData,
        password: "",
        otpCode: "",
        nextAction: LoginActions.ForgotPassword,
      }));
      setFormState(() => ({
        isShowOTP: true,
        isShowPassword: false,
        isShowModal: false,
        canResendOTP: false,
      }));
      setErrors((prev) => ({
        ...prev,
        responses: "",
      }));
      setCounter(response.data.ttlResend);
    }
  };
  const onResendOTP = async () => {
    loginSchema
      .validate({ phoneNumber: loginData.phoneNumber }, { abortEarly: false })
      .then(async (dataSchema) => {
        const response = await loginApi.verifyPhoneNumber(
          dataSchema.phoneNumber
        );
        console.log({ response });
        if (response.statusCode === 400) {
          toast({ type: "error", message: response.message });
        } else {
          setFormState((prevState) => ({
            ...prevState,
            canResendOTP: false,
          }));
          setErrors({});
          setCounter(60);
        }
      })
      .catch((errors) => {
        if (errors instanceof yup.ValidationError) {
          const errorMessage = errors.inner.reduce(
            (acc: object, current: any) => {
              return {
                ...acc,
                [current.path]: current.message,
              };
            },
            {}
          );
          setErrors(errorMessage);
        }
      });
  };
  const onHandleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (loginData.nextAction === LoginActions.CheckAccount) {
        loginSchema
          .validate(
            { phoneNumber: loginData.phoneNumber },
            { abortEarly: false }
          )
          .then(async (dataSchema) => {
            const checkAccount = await loginApi.checkAccount({
              phone: dataSchema.phoneNumber,
            });

            tracker({
              ...trackerParams,
              direct_object_id: "check_account",
              direct_object_property: dataSchema.phoneNumber,
              direct_object_type: "button",
              event_action: "click",
              on_screen: "home",
            });

            if (checkAccount.code === 0) {
              /**
               * is Registed
               * Next:
               * Check Promotions
               * Show password field (if valid)
               * Take user do the login action
               * Show popup Modal (no valid)
               */

              // const getOffer = await dispatch(
              //   fetchOffer({ phone: dataSchema.phoneNumber })
              // ).unwrap();

              // if (getOffer.error === 0 && getOffer.data.promotions) {
              //   //show password field
              //   setFormState((prevState) => ({
              //     ...prevState,
              //     isShowPassword: true,
              //     isShowOTP: false,
              //   }));
              //   setLoginData((formData) => ({
              //     ...formData,
              //     nextAction: LoginActions.Login,
              //   }));
              // } else {
              //   //Show popup Modal
              //   setFormState((prevState) => ({
              //     ...prevState,
              //     isShowModal: true,
              //   }));
              // }
              setFormState((prevState) => ({
                ...prevState,
                isShowPassword: true,
                isShowOTP: false,
              }));
              setLoginData((formData) => ({
                ...formData,
                fromAction: LoginActions.CheckAccount,
                nextAction: LoginActions.Login,
              }));
              setErrors({});
            }
            if (checkAccount.code === 1) {
              /**
               * New user
               * Next:
               * Send OTP code then show OTP field
               * Take user do the verify OTP action
               */
              const response = await dispatch(
                verifyPhoneNumber(dataSchema.phoneNumber)
              ).unwrap();
              if (response.statusCode === 400) {
                setErrors(() => ({
                  responses: response.message,
                }));
              } else {
                setFormState((prevState) => ({
                  ...prevState,
                  isShowOTP: true,
                  isShowPassword: false,
                  canResendOTP: false,
                }));
                setCounter(response.ttlResend);
                setErrors({});
                setLoginData((prevState) => ({
                  ...prevState,
                  fromAction: LoginActions.CheckAccount,
                  nextAction: LoginActions.VerifyOTP,
                }));
              }
            }
          })
          .catch((errors) => {
            if (errors instanceof yup.ValidationError) {
              const errorMessage = errors.inner.reduce(
                (acc: object, current: any) => {
                  return {
                    ...acc,
                    [current.path]: current.message,
                  };
                },
                {}
              );
              setErrors(errorMessage);
            }
          });
      } else {
        switch (loginData.nextAction) {
          case LoginActions.VerifyOTP: {
            loginSchema
              .validate(
                {
                  phoneNumber: loginData.phoneNumber,
                  otpCode: loginData.otpCode,
                  isVerifyAccount: true,
                },
                {
                  abortEarly: false,
                }
              )
              .then(async (dataSchema) => {
                const response = await dispatch(
                  createAccount({
                    phone: dataSchema.phoneNumber,
                    code: dataSchema.otpCode || "",
                  })
                ).unwrap();

                if (response?.data.statusCode === 400) {
                  setErrors(() => ({
                    responses: response.data.message,
                  }));
                } else {
                  setFormState((formState) => ({
                    ...formState,
                    isShowOTP: false,
                    isShowPassword: true,
                  }));
                  setErrors({});
                  setLoginData((formData) => ({
                    ...formData,
                    token: response?.token || "",
                    rfToken: response?.rfToken || "",
                    fromAction: LoginActions.VerifyOTP,
                    nextAction: LoginActions.CreatePassword,
                  }));
                }
              })
              .catch((errors) => {
                if (errors instanceof yup.ValidationError) {
                  const errorMessage = errors.inner.reduce(
                    (acc: object, current: any) => {
                      return {
                        ...acc,
                        [current.path]: current.message,
                      };
                    },
                    {}
                  );
                  setErrors(errorMessage);
                }
              });
            break;
          }
          case LoginActions.CreatePassword: {
            loginSchema
              .validate(
                {
                  phoneNumber: loginData.phoneNumber,
                  password: loginData.password || "",
                  isLogin: true,
                },
                {
                  abortEarly: false,
                }
              )
              .then(async (dataSchema) => {
                const response = await dispatch(
                  createPassword({
                    password: dataSchema.password || "",
                    token: loginData.token || "",
                  })
                ).unwrap();
                console.log({ response });
                localStorage.setItem(
                  StorageKEY.authToken,
                  loginData.token || ""
                );
                localStorage.setItem(
                  StorageKEY.refreshToken,
                  loginData.rfToken || ""
                );

                const userInfor = await dispatch(
                  fetchUserInfo(loginData.token)
                ).unwrap();

                const directObjectId =
                  loginData.fromAction === LoginActions.VerifyOTP
                    ? "register_result"
                    : "login_result";
                const onScreen =
                  loginData.fromAction === LoginActions.VerifyOTP
                    ? "home"
                    : "login";
                await tracker({
                  ...trackerParams,
                  direct_object_id: directObjectId,
                  direct_object_property: "success",
                  direct_object_type: "screen",
                  indirect_object_id: "mobile",
                  on_screen: onScreen,
                  indirect_object_property: dataSchema.phoneNumber,
                  user_id: userInfor.data.id,
                  event_action: "view",
                });
                localStorage.setItem(StorageKEY.uId, userInfor?.data?.id || "");
                dispatch(setBooking());
                //navigate("./checkout");
              })
              .catch((errors) => {
                if (errors instanceof yup.ValidationError) {
                  const errorMessage = errors.inner.reduce(
                    (acc: object, current: any) => {
                      return {
                        ...acc,
                        [current.path]: current.message,
                      };
                    },
                    {}
                  );
                  setErrors(errorMessage);
                }
              });
            break;
          }
          case LoginActions.Login: {
            loginSchema
              .validate(
                {
                  phoneNumber: loginData.phoneNumber,
                  password: loginData.password,
                  isLogin: true,
                },
                {
                  abortEarly: false,
                }
              )
              .then(async (dataSchema) => {
                const response = await dispatch(
                  makeLogin({
                    phone: dataSchema.phoneNumber,
                    password: dataSchema.password || "",
                  })
                ).unwrap();

                await tracker({
                  ...trackerParams,
                  direct_object_id: "sign_in",
                  direct_object_property: dataSchema.phoneNumber,
                  direct_object_type: "button",
                  event_action: "click",
                  on_screen: "home",
                  event_category: "payment",
                });
                let loginActionTracker = {
                  ...trackerParams,
                  direct_object_id: "login_result",
                  direct_object_type: "login",
                  indirect_object_id: "mobile",
                  on_screen: "login",
                  event_action: "view",
                  indirect_object_property: dataSchema.phoneNumber,
                };
                if (response?.data?.statusCode === 400) {
                  setErrors(() => ({
                    responses: response?.data?.message,
                  }));

                  await tracker({
                    ...loginActionTracker,
                    direct_object_property: "fail",
                  });
                } else {
                  //login success
                  await tracker({
                    ...loginActionTracker,
                    direct_object_property: "success",
                  });
                  setErrors({});
                  onResetAll();
                  dispatch(setBooking());

                  // tracker({
                  //   ...trackerParams,
                  //   direct_object_id: "login_result",
                  //   direct_object_property: "success",
                  //   direct_object_type: "login",
                  //   indirect_object_id: "mobile",
                  //   on_screen: 'login',
                  //   event_action: 'view',
                  //   indirect_object_property: dataSchema.phoneNumber,
                  //   user_id: response?.data?.id,
                  // });
                  // navigate("./checkout");
                }
              })
              .catch((errors) => {
                if (errors instanceof yup.ValidationError) {
                  const errorMessage = errors.inner.reduce(
                    (acc: object, current: any) => {
                      return {
                        ...acc,
                        [current.path]: current.message,
                      };
                    },
                    {}
                  );
                  setErrors(errorMessage);
                }
              });
            break;
          }
          case LoginActions.ForgotPassword: {
            loginSchema
              .validate(
                {
                  phoneNumber: loginData.phoneNumber,
                  otpCode: loginData.otpCode,
                  isVerifyAccount: true,
                },
                { abortEarly: false }
              )
              .then(async (dataSchema) => {
                const response = await dispatch(
                  loginWithOtpCode({
                    phone: dataSchema.phoneNumber,
                    code: dataSchema.otpCode || "",
                  })
                ).unwrap();

                if (response?.data?.statusCode === 400) {
                  setErrors(() => ({
                    responses: response?.data?.message,
                  }));
                } else {
                  setFormState((prevState) => ({
                    ...prevState,
                    isShowPassword: true,
                    isShowOTP: false,
                  }));
                  setLoginData((formData) => ({
                    ...formData,
                    password: "",
                    otpCode: "",
                    token: response.token,
                    fromAction: LoginActions.ForgotPassword,
                    nextAction: LoginActions.CreatePassword,
                  }));
                  setErrors({});
                }
              })
              .catch((errors) => {
                if (errors instanceof yup.ValidationError) {
                  const errorMessage = errors.inner.reduce(
                    (acc: object, current: any) => {
                      return {
                        ...acc,
                        [current.path]: current.message,
                      };
                    },
                    {}
                  );
                  setErrors(errorMessage);
                }
              });

            break;
          }
        }
      }
    },
    [loginData, formState]
  );
  //makge payment
  const handleSubmitPayment = async ({
    methodId,
    offer,
    token,
  }: {
    methodId: string;
    offer: OfferItemType;
    token?: string;
  }) => {
    if (isPaymentLoading) return;
    setPaymentLoading(true);
    let indirectProperty = "";
    switch (channelType) {
      case PageName.VNPAY: {
        indirectProperty = "vnpay";
        break;
      }
      case PageName.SHOPEE: {
        indirectProperty = "shopeepay";
        break;
      }
      case PageName.ZALO: {
        indirectProperty = "zalopay";
        break;
      }
    }
    let trackerData = {
      ...trackerParams,
      on_screen: "payment_result",
      direct_object_type: "package",
      indirect_object_property: indirectProperty,
      indirect_object_type: "result",
      direct_object_property: offer?.productName,
      direct_object_id: offer?.productId,
      indirect_object_id: "",
      event_action: "buy",
      context_value: {
        price: offer?.price,
      },
    };
    try {
      const response = await checkoutApi.makePayment({
        methodId,
        offer,
        token,
      });
      if (response.error === 0) {
        trackerData = {
          ...trackerData,
          indirect_object_id: "success",
        };
        navigate(`/${channelType}/thankyou`);
      } else {
        console.log("fail payment", response);
        trackerData = {
          ...trackerData,
          indirect_object_id: "failed",
        };
        setPaymentFail(true);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setPaymentLoading(false);
      tracker({
        ...trackerData,
      });
    }
  };
  useEffect(() => {
    if (counter !== 0) {
      timeOutRef.current = setTimeout(() => {
        setCounter((count) => count - 1);
        clearTimeout(timeOutRef.current);
      }, 1000);
    } else {
      setFormState((prevState) => ({
        ...prevState,
        canResendOTP: true,
      }));
    }
  }, [counter]);
  useEffect(() => {
    const beforeChannelName = localStorage.getItem(StorageKEY.channelName);

    if (beforeChannelName !== channelType) {
      dispatch(logout());
      dispatch(resetBooking());
      window.location.reload();
    }

    channelType && dispatch(setChannelName(channelType));
  }, [channelType]);

  //handle redirect payment
  useEffect(() => {
    (async () => {
      const offer = JSON.parse(
        localStorage.getItem(StorageKEY.offer) || JSON.stringify({})
      );
      const userId = localStorage.getItem(StorageKEY.uId);
      const token = localStorage.getItem(StorageKEY.authToken);
      if (isEmpty(offer) || userId === null || token === null) return;

      /**
       * paymentmethods
       */
      if (channelType === PageName.SHOPEE) {
        const resultCode = searchParams.get("result") || "0";
        const authCode = searchParams.get("authCode") || "";
        const requestId = searchParams.get("requestId");
        // console.log({ resultCode, authCode, requestId, token });
        if (userId && resultCode === "100" && requestId && authCode) {
          const walletInfor = await checkoutApi.getAcountWalletInforShopee({
            authCode: authCode,
            requestId: requestId,
            userId: userId,
          });

          if (walletInfor.error === 0) {
            await handleSubmitPayment({
              methodId: walletInfor.data.methodId,
              offer: offer,
              token: authToken,
            });
          } else {
            setPaymentFail(true);
          }
        }
      }
      if (channelType === PageName.VNPAY) {
        const responseCode = searchParams.get("vnp_ResponseCode") || "";
        const transactionStatus =
          searchParams.get("vnp_TransactionStatus") || "";
        let trackerData = {
          ...trackerParams,
          on_screen: "payment_result",
          direct_object_type: "package",
          indirect_object_property: "vnpay",
          indirect_object_type: "result",
          direct_object_property: offer?.productName,
          direct_object_id: offer?.productId,
          indirect_object_id: "",
          event_action: "buy",
          context_value: {
            price: offer?.price,
          },
        };

        if (responseCode && transactionStatus) {
          if (responseCode === "00" && transactionStatus === "00") {
            await tracker({
              ...trackerData,
              indirect_object_id: "success",
            });
            navigate(`/${channelType}/thankyou`);
          } else {
            await tracker({
              ...trackerData,
              indirect_object_id: "failed",
            });
            setPaymentFail(true);
          }
        }
      }
      if (channelType === PageName.ZALO) {
        const status = searchParams.get("status") || "0";
        const bindingId = searchParams.get("binding_id") || "";
        if (status && bindingId) {
          const response = await checkoutApi.fetchChanelAndMethod();

          if (response.error === 0) {
            const { channel, method } = response.data;
            const channelCampaign =
              channel.find((item) => item.type === PAYMENT_CHANELS.ZALOPAY) ||
              {};

            const methodCampaign =
              method.find((item) => item.channelId === channelCampaign.id) ||
              {};
            if (status === "1") {
              /**
               * SUBMIT PAYMENT
               */
              if (methodCampaign && !isEmpty(methodCampaign)) {
                await handleSubmitPayment({
                  methodId: methodCampaign?.methodId || "",
                  offer: offer,
                  token: authToken,
                });
              }
            }
          }
        }
      }
    })();
  }, [channelType]);
  useEffect(() => {
    if (sessionId && ipAddress && channelType) {
      tracker({
        ...trackerParams,
        client_ip: ipAddress || "",
        session_id: sessionId || "",
        direct_object_type: "screen",
        event_action: "view",
        on_screen: "home",
      });
    }
  }, [sessionId, ipAddress, channelType]);

  useEffect(() => {
    const reloadPage = JSON.parse(
      localStorage.getItem(StorageKEY.reloadBooking) || JSON.stringify(false)
    );

    if (isLogedin && reloadPage) {
      dispatch(logout());
      dispatch(resetBooking());
    }
  }, [isLogedin]);

  return (
    <div className={`page ${channelType}`}>
      <div className="inner-page">
        <div className="section section-top">
          <Container>
            <div className="section-inner">
              <div className="section-body">
                <Grid verticalAlign="middle" centered>
                  <Grid.Column
                    mobile={16}
                    tablet={16}
                    computer={8}
                    className="image-column"
                  >
                    <Image
                      src={`${process.env.PUBLIC_URL}/images/first-left.png`}
                      alt="Galaxy Play - Art nha ba nu"
                    />
                  </Grid.Column>
                  <Grid.Column
                    mobile={16}
                    tablet={16}
                    computer={8}
                    className="content-column"
                  >
                    <div className="innter-column">
                      <div className="glx-section center">
                        <Image
                          src={`${process.env.PUBLIC_URL}/images/logo-glx-white.svg`}
                          alt="Galaxy play logo"
                          className="logo"
                        />
                        <p className="subtitle white">
                          Nền tảng xem phim online số 1 tại Việt Nam
                        </p>
                        <div
                          className="image-art"
                          style={{ cursor: "pointer" }}
                        >
                          <Image
                            src={`${process.env.PUBLIC_URL}/images/${channelType}/art-nbn.png`}
                            alt="Galaxy - nha ba nu"
                            className="logo"
                            onClick={() => {
                              paymentRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </Grid.Column>
                </Grid>
              </div>
            </div>
          </Container>
        </div>
        <SliderSection channelType={channelType || ""} />
        <div className="section section-chanel" ref={paymentRef}>
          <Container>
            <Grid verticalAlign="middle" centered>
              <Grid.Column
                mobile={16}
                tablet={16}
                computer={8}
                className="image-column"
              >
                <Image
                  src={`${process.env.PUBLIC_URL}/images/second-left.png`}
                  alt="nha ba nu"
                />
              </Grid.Column>
              <Grid.Column
                mobile={16}
                tablet={16}
                computer={8}
                className="register-column"
              >
                <div className="inner-column">
                  {(!isLogedin && (
                    <>
                      <div className="partner-section center">
                        <p className="title upercase white">
                          <span>Đặc quyền</span>
                          <span>dành cho khách hàng</span>
                        </p>
                        <Image
                          src={`${process.env.PUBLIC_URL}/images/${channelType}/logo-partner-origin.png`}
                          alt={channelType}
                          className={`logo ${channelType}`}
                        />
                      </div>
                      <LoginForm
                        onHandleSubmit={onHandleSubmit}
                        data={loginData}
                        errors={errors}
                        formState={formState}
                        onChange={onChange}
                        onForgotPassword={onForgotPassword}
                        counter={counter}
                        onChangePhoneNumber={onResetAll}
                        onResendOTP={onResendOTP}
                        ref={inputRef}
                      />
                    </>
                  )) || (
                    <>
                      <CheckOut
                        userData={userData}
                        isLogedin={isLogedin}
                        channelType={channelType}
                        onSubmitPayment={handleSubmitPayment}
                        isPaymentProcess={isPaymentLoading}
                        isBooking={isBooking}
                        ipAddress={ipAddress}
                        sessionId={sessionId}
                      />
                    </>
                  )}
                </div>
              </Grid.Column>
            </Grid>
          </Container>
        </div>

        <Modal
          title="Rất tiếc"
          isCenter={true}
          isOpen={formState.isShowModal}
          width={400}
          onClose={() =>
            setFormState((prevState) => ({ ...prevState, isShowModal: false }))
          }
          onCancel={() =>
            setFormState((prevState) => ({ ...prevState, isShowModal: false }))
          }
          render={() => <ModalContent />}
        />
        <Modal
          title="Thanh toán thất bại"
          isShowCloseIcon={false}
          isCenter={true}
          isOpen={isPaymentFail}
          width={400}
          onClose={() => window.location.reload()}
          onSubmit={() => {
            dispatch(logout());
            dispatch(resetBooking());
            navigate(`/${channelType}`);
            setPaymentFail(false);
          }}
          render={() => (
            <>
              <p>
                Quá trình thanh toán bị thất bại, Quý Khách vui lòng thử lại.
              </p>
            </>
          )}
        />
        <PaymentLoading isLoading={isPaymentLoading} />
      </div>
    </div>
  );
};
export default ChanelPage;
